/** The parts of a spec (v1 or v2) the example prompts read. */
type PromptSpec = {
  readonly rows: ReadonlyArray<{
    readonly widgets: ReadonlyArray<{ readonly kind: string; readonly title?: string | undefined }>;
  }>;
};

export type DashboardAssistantMode = "edit" | "ask";

const MAX_PROMPTS = 3;

function titled(spec: PromptSpec, kind: string): ReadonlyArray<string> {
  return spec.rows
    .flatMap((row) => row.widgets)
    .filter((widget) => widget.kind === kind && widget.title)
    .map((widget) => widget.title!);
}

/**
 * Example prompts named after this dashboard's own tiles, so the first thing
 * the person reads is something this dashboard can do. Viewers get questions
 * only; editors get changes first.
 */
export function dashboardAssistantPrompts(
  spec: PromptSpec,
  mode: DashboardAssistantMode,
): ReadonlyArray<string> {
  const [kpi, secondKpi] = titled(spec, "kpi");
  const [chart] = titled(spec, "chart");
  const [table] = titled(spec, "table");
  const prompts =
    mode === "edit"
      ? [
          chart ? `Split ${chart} by navigator` : null,
          kpi ? `Compare ${kpi} to last month` : null,
          secondKpi ? `Add a weekly chart of ${secondKpi}` : null,
          table ? `Sort ${table} by a different column` : null,
          "Compare to last month",
        ]
      : [
          kpi ? `How did ${kpi} change since last month?` : null,
          chart ? `What stands out in ${chart}?` : null,
          table ? `Which rows in ${table} need attention?` : null,
          "What changed most this week?",
        ];
  return [...new Set(prompts.filter((prompt): prompt is string => prompt !== null))].slice(
    0,
    MAX_PROMPTS,
  );
}
